import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { Add, Edit, Visibility, Search } from '@mui/icons-material';
import { servicesApi } from '../services/services';
import { Service, ServiceForm } from '../types';
import { format } from 'date-fns';

const ServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<ServiceForm & {
    type?: string;
    is_active?: boolean;
    identifier?: string;
    category?: string;
  }>({
    name: '',
    description: '',
    price: 0,
    type: 'Custom',
    is_active: true,
    identifier: '',
    category: ''
  });

  const loadServices = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await servicesApi.getServices(false);
      setAllServices(data);
    } catch (err: any) {
      console.error('Error loading services:', err);
      setError(err.response?.data?.detail || 'Error loading services');
    } finally {
      setLoading(false);
    }
  };

  const filterServices = (data: Service[], search: string, onlyActive: boolean) => {
    let filtered = [...data];
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(service => 
        service.name?.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower) ||
        service.category?.toLowerCase().includes(searchLower)
      );
    }
    
    // Active filter
    if (onlyActive) {
      filtered = filtered.filter(service => service.is_active);
    }
    
    setServices(filtered);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      filterServices(allServices, searchTerm, activeOnly);
    }
  };

  useEffect(() => {
    filterServices(allServices, searchTerm, activeOnly);
  }, [allServices, searchTerm, activeOnly]);

  const loadServiceDetails = async (id: number) => {
    try {
      setFormLoading(true);
      const service = await servicesApi.getService(id);
      setSelectedService(service);
      return service;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading service data');
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, [activeOnly]);

  const handleOpenService = async (serviceId: number) => {
    const service = await loadServiceDetails(serviceId);
    if (service) {
      setIsViewMode(false); // Все могут редактировать услуги
      setFormData({
        name: service.name,
        description: service.description || '',
        price: service.price || 0,
        type: service.type || 'Custom',
        is_active: service.is_active,
        identifier: service.identifier || '',
        category: service.category || ''
      });
      setOpenEditDialog(true);
    }
  };

  const handleAddService = () => {
    setSelectedService(null);
    setIsViewMode(false);
    setFormData({
      name: '',
      description: '',
      price: 0,
      type: 'Custom',
      is_active: true,
      identifier: '',
      category: ''
    });
    setOpenEditDialog(true);
  };

  const handleSaveService = async () => {
    try {
      setFormLoading(true);
      setError('');
      
      const serviceData: any = {
        name: formData.name,
        description: formData.description || undefined,
        price: formData.price || undefined,
        type: formData.type,
        is_active: formData.is_active,
        category: formData.category || undefined
      };
      
      // When creating a new service, add identifier if specified
      if (!selectedService && formData.identifier) {
        serviceData.identifier = formData.identifier;
      }
      
      if (selectedService) {
        // Update existing service - don't send identifier as it cannot be changed
        await servicesApi.updateService(selectedService.id, serviceData);
      } else {
        // Create new service
        await servicesApi.createService(serviceData);
      }
      
      setOpenEditDialog(false);
      await loadServices();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error saving service');
    } finally {
      setFormLoading(false);
    }
  };


  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  if (loading && services.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Services
        </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddService}
          >
          Add Service
          </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search by name, description or category..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            filterServices(allServices, e.target.value, activeOnly);
          }}
          onKeyPress={handleSearchKeyPress}
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                filterServices(allServices, searchTerm, e.target.checked);
              }}
            />
          }
            label="Show only active"
        />
        {searchTerm && (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSearchTerm('');
              filterServices(allServices, '', activeOnly);
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No services found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>{service.id}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      sx={{ 
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handleOpenService(service.id)}
                    >
                      {service.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                      {service.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {service.category ? (
                      <Chip label={service.category} size="small" variant="outlined" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.type === 'Default' ? 'Default' : 'Custom'}
                      size="small"
                      color={service.type === 'Default' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.is_active ? 'Active' : 'Inactive'}
                      color={service.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create/View Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setIsViewMode(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedService ? (isViewMode ? 'View Service' : 'Edit Service') : 'Add Service'}
        </DialogTitle>
        <DialogContent>
          {formLoading && !selectedService ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Service Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={3}
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    inputProps={{ step: '0.01', min: '0' }}
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Service Type</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      label="Service Type"
                      disabled={isViewMode}
                    >
                      <MenuItem value="Custom">Custom</MenuItem>
                      <MenuItem value="Default">Default</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="CRM, Trading, Sales, etc."
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Identifier"
                    value={formData.identifier}
                    onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                    placeholder="crm_basic_access, copy_trading, etc."
                    disabled={isViewMode || (!!selectedService && !!selectedService.identifier)}
                    helperText={selectedService?.identifier 
                      ? "Identifier cannot be changed after service creation" 
                      : "Unique identifier for default services"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        disabled={isViewMode}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setIsViewMode(false);
          }} disabled={formLoading}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
          <Button
            onClick={handleSaveService}
            variant="contained"
            disabled={formLoading || !formData.name}
          >
              {formLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ServicesPage;
